import { Fragment } from 'react';
import { withVersionHeaders } from '../../lib/changelogGroups';
import { useAuthStore } from '../../store/authStore';
import { MainLayout } from '../../components/MainLayout';
import { WeeklyCalendar } from '../../components/WeeklyCalendar';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '@werkstatt/ui';
import {
  Car,
  Calendar,
  Users,
  FileText,
  Receipt,
  TrendingUp,
  Wrench,
} from 'lucide-react';

import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import api from '../../lib/api';

interface ChangelogEntry {
  id: number;
  git_hash: string;
  title: string;
  category: string;
  scope: string | null;
  version?: string | null;
  icon: string;
  created_at: string;
}

interface DashboardStats {
  total_vehicles: number;
  available_vehicles: number;
  rented_vehicles: number;
  active_bookings: number;
  pending_bookings: number;
  total_bookings: number;
  total_customers: number;
  active_customers: number;
  active_contracts: number;
  total_contracts: number;
  unpaid_invoices: number;
  overdue_invoices: number;
  total_invoices: number;
  total_revenue_cents: number;
  monthly_revenue_cents: number;
  upcoming_returns: number;
}

export default function Dashboard() {
  const { user } = useAuthStore();
  const navigate = useNavigate();

  // Fetch dashboard stats
  const { data: statsData, isLoading } = useQuery({
    queryKey: ['dashboard-stats'],
    queryFn: async () => {
      const response = await fetch('/api/dashboard/stats', {
        credentials: 'include',
      });
      if (!response.ok) throw new Error('Failed to fetch dashboard stats');
      return response.json() as Promise<DashboardStats>;
    },
  });

  const { data: changelog = [] } = useQuery({
    queryKey: ['changelog'],
    queryFn: async () => (await api.get<ChangelogEntry[]>('/changelog')).data,
    staleTime: 5 * 60 * 1000,
  });

  const formatCurrency = (cents: number) => {
    return new Intl.NumberFormat('de-DE', {
      style: 'currency',
      currency: 'EUR',
    }).format(cents / 100);
  };

  const stats = [
    {
      title: 'Fahrzeuge',
      description: 'Verfügbare Mietfahrzeuge',
      value: isLoading ? '...' : statsData?.available_vehicles.toString() || '0',
      total: statsData?.total_vehicles.toString() || '0',
      icon: Car,
      color: 'text-blue-600',
      bgColor: 'bg-blue-500/15',
      href: '/vehicles',
    },
    {
      title: 'Aktive Buchungen',
      description: 'Laufende Mieten',
      value: isLoading ? '...' : statsData?.active_bookings.toString() || '0',
      icon: Calendar,
      color: 'text-green-600',
      bgColor: 'bg-green-500/15',
      href: '/bookings',
    },
    {
      title: 'Kunden',
      description: 'Registrierte Kunden',
      value: isLoading ? '...' : statsData?.total_customers.toString() || '0',
      icon: Users,
      color: 'text-purple-600',
      bgColor: 'bg-purple-500/15',
      href: '/customers',
    },
    {
      title: 'Verträge',
      description: 'Aktive Verträge',
      value: isLoading ? '...' : statsData?.active_contracts.toString() || '0',
      icon: FileText,
      color: 'text-orange-600',
      bgColor: 'bg-orange-500/15',
      href: '/contracts',
    },
    {
      title: 'Rechnungen',
      description: 'Offene Rechnungen',
      value: isLoading ? '...' : statsData?.unpaid_invoices.toString() || '0',
      icon: Receipt,
      color: 'text-red-600',
      bgColor: 'bg-red-500/15',
      href: '/invoices',
    },
    {
      title: 'Umsatz (Monat)',
      description: 'Aktueller Monat',
      value: isLoading ? '...' : formatCurrency(statsData?.monthly_revenue_cents || 0),
      icon: TrendingUp,
      color: 'text-teal-600',
      bgColor: 'bg-teal-500/15',
      href: '/invoices',
    },
  ];

  return (
    <MainLayout>
      <div className="space-y-6">
        {/* Welcome Section */}
        <div>
          <h1 className="font-display text-3xl font-bold">
            Willkommen zurück{user?.first_name ? `, ${user.first_name}` : ''}!
          </h1>
          <p className="text-muted-foreground mt-2">
            Hier ist Ihre RentalHub Übersicht
          </p>
        </div>

        {/* Weekly Calendar */}
        <WeeklyCalendar />

        {/* Stats Grid */}
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {stats.map((stat) => (
            <Card
              key={stat.title}
              variant="glass"
              className="hover:shadow-glow transition-shadow cursor-pointer"
              onClick={() => navigate(stat.href)}
            >
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="font-display text-sm font-medium">
                  {stat.title}
                </CardTitle>
                <div className={`p-2 rounded-lg ${stat.bgColor}`}>
                  <stat.icon className={`h-4 w-4 ${stat.color}`} />
                </div>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{stat.value}</div>
                <p className="text-xs text-muted-foreground mt-1">
                  {stat.description}
                  {stat.total && ` von ${stat.total}`}
                </p>
              </CardContent>
            </Card>
          ))}
        </div>

        {changelog.length > 0 && (
          <Card variant="glass">
            <CardHeader>
              <CardTitle className="font-display flex items-center gap-2 text-base">
                <Wrench className="h-4 w-4 text-muted-foreground" />
                Letzte Änderungen & Updates
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ul className="space-y-2">
                {withVersionHeaders(changelog.slice(0, 20)).map(({ entry, header }) => {
                  const categoryBadge: Record<string, { label: string; cls: string }> = {
                    feature:     { label: 'Feature',  cls: 'bg-blue-500/15 text-blue-600' },
                    bugfix:      { label: 'Bug',      cls: 'bg-red-500/15 text-red-600' },
                    improvement: { label: 'Update',   cls: 'bg-amber-500/15 text-amber-600' },
                    security:    { label: 'Security', cls: 'bg-orange-500/15 text-orange-600' },
                  };
                  const badge = categoryBadge[entry.category]
                    ?? { label: entry.category, cls: 'bg-muted text-muted-foreground' };
                  return (
                    <Fragment key={entry.id}>
                    {header && (
                      <li className="pt-2 first:pt-0 font-mono text-xs font-semibold text-muted-foreground">v{header}</li>
                    )}
                    <li className="flex items-start gap-2 text-sm">
                      <span className="shrink-0 mt-0.5">
                        <span className={`text-xs font-medium px-2 py-0.5 rounded-pill ${badge.cls}`}>
                          {badge.label}
                        </span>
                      </span>
                      <span className="text-foreground min-w-0 flex-1">{entry.title}</span>
                      <span className="shrink-0 text-xs text-muted-foreground whitespace-nowrap">
                        {new Date(entry.created_at).toLocaleDateString('de-DE', {
                          day: '2-digit', month: '2-digit', year: '2-digit',
                        })}
                      </span>
                    </li>
                    </Fragment>
                  );
                })}
              </ul>
            </CardContent>
          </Card>
        )}
      </div>
    </MainLayout>
  );
}
